import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    contentChildren,
    inject,
    input,
    linkedSignal,
    model,
    output,
    TemplateRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { IconSanitizerService } from '../../services';
import { TabItemComponent } from './tab-item/tab-item.component';

export interface ITabPaginationConfig {
    currentPage: number;
    pagesCount: number;
    totalCount: number;
    pageSize?: number;
}

export class TabChangeArgs {
    canChange?: Promise<boolean>;

    constructor(
        public readonly currentTabIndex: number,
        public readonly futureTabIndex: number,
        public readonly currentTabName?: string,
        public readonly futureTabName?: string
    ) {
        this.canChange = undefined;
    }
}

@Component({
    selector: 'lib-tab-view',
    templateUrl: './tab-view.component.html',
    styleUrls: ['./tab-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe]
})
export class TabViewComponent {
    private readonly iconSanitizer: IconSanitizerService = inject(IconSanitizerService);

    readonly tabs = contentChildren(TabItemComponent);

    readonly currentTab = model('');
    // Fixed content that doesn't re render regardless current tab position.
    readonly fixedContentHeader = input<TemplateRef<unknown>>();
    readonly hasSteps = input(false);
    readonly hasQuantity = input(false);
    readonly direction = input<'row' | 'column'>('row');
    readonly alignment = input<'start' | 'center' | 'end'>('start');
    readonly separation = input<'narrow' | 'normal' | 'wide'>('normal');
    readonly containerBackground = input(false);
    readonly renderAll = input(false);
    readonly paginationConfig = model<ITabPaginationConfig | undefined>();
    readonly tabViewId = input<string | undefined>();

    readonly beforeTabChange = output<TabChangeArgs>();

    readonly activeTab = linkedSignal(() => {
        const tabs = this.tabs();
        const name = this.currentTab();
        if (!tabs.length) return undefined;
        if (name) {
            const found = tabs.find((tab) => tab.tabName() === name);
            if (found) return found;
        }
        return tabs[0];
    });

    readonly separatorIcon: SafeHtml = this.iconSanitizer.getIcon('separator');

    readonly containerClasses = computed(() =>
        ['tabContainer', this.direction() === 'column' ? 'column' : ''].filter(Boolean).join(' ')
    );

    readonly navClasses = computed(() => {
        const classes = [
            'nav',
            this.hasSteps() ? 'hasSteps' : '',
            this.hasQuantity() ? 'hasQuantity' : '',
            this.direction() === 'column' ? 'column' : 'row',
            this.alignment() === 'start' ? 'alignStart' : '',
            this.alignment() === 'center' ? 'alignCenter' : '',
            this.alignment() === 'end' ? 'alignEnd' : '',
            this.separation() === 'narrow' ? 'narrowSeparation' : '',
            this.separation() === 'normal' ? 'normalSeparation' : '',
            this.separation() === 'wide' ? 'wideSeparation' : '',
            this.containerBackground() ? 'containerBackground' : ''
        ];

        return classes.filter(Boolean).join(' ');
    });

    readonly tabNameClasses = computed(() => ['tabName', this.direction() === 'column' ? 'column' : 'row'].join(' '));

    getTabsNames(): string[] {
        return this.tabs().map((tab) => tab.tabName());
    }

    async setActiveComponent(value: TabItemComponent): Promise<void> {
        if (this.activeTab() === value) return;

        const current = this.activeTab();
        const currentTabIndex = this.getCurrentTabIndex();
        const currentTabName = current?.tabName();
        const futureTabIndex = this.getTabIndex(value);
        const futureTabName = value.tabName();

        // we send and event informing that the tab change will happen, but let the parent control
        // decide if that change can happen or not.
        const eventArgs = new TabChangeArgs(currentTabIndex, futureTabIndex, currentTabName, futureTabName);
        this.beforeTabChange.emit(eventArgs);

        if (eventArgs.canChange) {
            const result = await eventArgs.canChange;
            if (!result) return;
        }

        this.activeTab.set(value);
        this.currentTab.set(futureTabName);
    }

    nextTab(): void {
        const currentIndex = this.getCurrentTabIndex();
        // Current shouldn't be the last one
        if (currentIndex >= 0 && currentIndex <= this.tabs().length - 2) {
            this.setActiveComponentByIndex(currentIndex + 1);
        }
    }

    previousTab(): void {
        const currentIndex = this.getCurrentTabIndex();
        // At least 1 to be able to go back to first tab
        if (currentIndex >= 1) {
            this.setActiveComponentByIndex(currentIndex - 1);
        }
    }

    goNext(): void {
        if (!this.canGoNext()) return;
        this.changePage(this.paginationConfig()!.currentPage + 1);
    }

    private setActiveComponentByIndex(futureTabIndex: number): void {
        const activeTab = this.getTabByIndex(futureTabIndex);
        if (activeTab && !activeTab.disabled()) {
            this.setActiveComponent(activeTab);
        }
    }

    private getTabByIndex(index: number): TabItemComponent | undefined {
        return this.tabs()[index];
    }

    private getTabIndex(tab: TabItemComponent): number {
        return this.tabs().indexOf(tab);
    }

    private getCurrentTabIndex(): number {
        const active = this.activeTab();
        return active ? this.getTabIndex(active) : -1;
    }

    private canGoNext(): boolean {
        const config = this.paginationConfig();
        if (!config?.currentPage || !config?.pagesCount) return false;
        return config.currentPage < config.pagesCount;
    }

    private changePage(page: number): void {
        const config = this.paginationConfig();
        if (!config) return;
        this.paginationConfig.set({ ...config, currentPage: page });
    }
}
